export type SectionKey =
    | 'experience'
    | 'education'
    | 'skills'
    | 'languages'
    | 'interests'
    | 'references'
    | 'summary';

export interface CVLayout {
    pageCount: number;
    itemPages: Record<string, number>;
    sectionPages?: Partial<Record<SectionKey, number>>;
}

export interface CVData {
    title: string;
    personalInfo: {
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        address: string;
        city: string;
        country: string;
        profileImage?: string;
        profession: string;
        summary: string;
        website?: string;
    };
    education: Education[];
    experience: Experience[];
    skills: Skill[];
    languages: Language[];
    interests?: Interest[];
    references?: Reference[];
    style?: {
        primaryColor?: string;
        secondaryColor?: string;
        fontFamily?: string;
        textColor?: string;
        backgroundColor?: string;
        sectionBackgroundColor?: string;
        imageBorderColor?: string;
        /** Tailles relatives (100 = défaut) par partie du CV */
        fontSizes?: Partial<Record<
            | 'name'
            | 'profession'
            | 'heading'
            | 'summary'
            | 'experience'
            | 'education'
            | 'skills'
            | 'languages'
            | 'contact'
            | 'interests'
            | 'references',
            number
        >>;
    };
    layout?: CVLayout;
}

export interface Education {
    id?: string;
    institution: string;
    degree: string;
    field: string;
    startDate: string;
    endDate: string;
    description: string;
    isCurrent: boolean;
}

export interface Experience {
    id?: string;
    company: string;
    position: string;
    startDate: string;
    endDate: string;
    description: string;
    isCurrent: boolean;
}

export interface Skill {
    id?: string;
    name: string;
    level: number; // 0-100 for percentage-based templates
    category?: string;
}

export interface Language {
    id?: string;
    name: string;
    level: number; // 0-100 for percentage
}

export interface Interest {
    id?: string;
    name: string;
}

export interface Reference {
    id?: string;
    name: string;
    position: string;
    company: string;
    phone: string;
    email: string;
}

export interface TemplateProps {
    data: CVData;
    isContinuationPage?: boolean;
    /** Index de page A4 (0-based) pour la continuité des titres de section */
    pageIndex?: number;
    /** Données complètes (toutes pages) pour savoir si une section continue */
    fullData?: CVData;
    /** true = afficher le modèle démo ; false dès que l'utilisateur saisit */
    showPlaceholders?: boolean;
}

// Template configuration to define which fields each template uses
export interface TemplateConfig {
    id: string;
    name: string;
    color: string;
    hex: string;
    fields: {
        skills: boolean;
        languages: boolean;
        interests: boolean;
        references: boolean;
        website: boolean;
    };
}

export const TEMPLATE_CONFIGS: TemplateConfig[] = [
    {
        id: 'ocean-wave',
        name: 'Ocean Wave',
        color: 'bg-cyan-500',
        hex: '#06b6d4',
        fields: { skills: true, languages: false, interests: true, references: true, website: true }
    },
    {
        id: 'modern-blue',
        name: 'Modern Blue',
        color: 'bg-blue-600',
        hex: '#2563eb',
        fields: { skills: true, languages: false, interests: true, references: false, website: true }
    },
    {
        id: 'geometric-red',
        name: 'Geometric Red',
        color: 'bg-red-600',
        hex: '#dc2626',
        fields: { skills: false, languages: false, interests: false, references: false, website: true }
    },
    {
        id: 'creative-crimson',
        name: 'Creative Crimson',
        color: 'bg-rose-900',
        hex: '#881337',
        fields: { skills: true, languages: true, interests: false, references: true, website: false }
    },
    {
        id: 'professional-edge',
        name: 'Professional Edge',
        color: 'bg-red-700',
        hex: '#b91c1c',
        fields: { skills: true, languages: true, interests: false, references: true, website: false }
    },
    {
        id: 'red-hexagon',
        name: 'Red Hexagon',
        color: 'bg-red-600',
        hex: '#dc2626',
        fields: { skills: true, languages: false, interests: false, references: true, website: true }
    },
    {
        id: 'corporate-blue',
        name: 'Corporate Blue',
        color: 'bg-blue-900',
        hex: '#1e3a8a',
        fields: { skills: true, languages: true, interests: false, references: true, website: false }
    },
    {
        id: 'teal-professional',
        name: 'Teal Professional',
        color: 'bg-teal-700',
        hex: '#0f766e',
        fields: { skills: true, languages: true, interests: false, references: true, website: false }
    },
    {
        id: 'earth-tones',
        name: 'Earth Tones',
        color: 'bg-yellow-700',
        hex: '#a16207',
        fields: { skills: true, languages: true, interests: false, references: false, website: true }
    },
    {
        id: 'sky-blue',
        name: 'Sky Blue',
        color: 'bg-sky-500',
        hex: '#0ea5e9',
        fields: { skills: true, languages: true, interests: true, references: false, website: true }
    },
    {
        id: 'golden-curve',
        name: 'Golden Curve',
        color: 'bg-orange-500',
        hex: '#f97316',
        fields: { skills: true, languages: true, interests: true, references: true, website: true }
    },
    {
        id: 'crimson-wave',
        name: 'Crimson Wave',
        color: 'bg-red-800',
        hex: '#b22222',
        fields: { skills: true, languages: true, interests: true, references: false, website: true }
    },
    {
        id: 'magenta-ribbon',
        name: 'Magenta Ribbon',
        color: 'bg-pink-700',
        hex: '#c11a5b',
        fields: { skills: true, languages: true, interests: true, references: true, website: true }
    },
    {
        id: 'red-navy',
        name: 'Red Navy',
        color: 'bg-red-600',
        hex: '#E52232',
        fields: { skills: false, languages: false, interests: false, references: false, website: true }
    },
    {
        id: 'ivoirien-elegant',
        name: 'Élégance Ivoirienne',
        color: 'bg-green-800',
        hex: '#166534',
        fields: { skills: true, languages: true, interests: false, references: false, website: false }
    },
    {
        id: 'abidjan-corporate',
        name: 'Abidjan Corporate',
        color: 'bg-slate-800',
        hex: '#1e3a5f',
        fields: { skills: true, languages: true, interests: false, references: true, website: true }
    },
    {
        id: 'sahel-modern',
        name: 'Sahel Moderne',
        color: 'bg-orange-800',
        hex: '#9a3412',
        fields: { skills: true, languages: false, interests: true, references: false, website: false }
    },
];
